import { Badge, SimpleGrid, Stack, Table, Tabs, Text } from "@mantine/core";
import { IconCash, IconClock, IconCoin } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { DataTableShell, FilterBar, PageHeader, SortableTh, StatCard, useInfinitePage, useTableSort } from "../../components/ui";
import { earningsApi } from "../../lib/api";
import { NAV_COPY, NAV_ICONS } from "../../lib/navCopy";
import { formatDate, formatPaise, PAYOUT_STATUS_LABELS, payoutStatusColor } from "../../lib/format";
import { useQueryFilters } from "../../lib/useQueryFilters";

const AFFILIATE_PAYMENT_TABS = [
  { value: "pending", label: "Pending" },
  { value: "received", label: "Received" },
  { value: "all", label: "All" },
] as const;

type TabValue = (typeof AFFILIATE_PAYMENT_TABS)[number]["value"];

const AFFILIATE_PAYMENT_TAB_DEFAULT: TabValue = "pending";
const AFFILIATE_PAYMENT_FILTER_DEFAULTS = { tab: AFFILIATE_PAYMENT_TAB_DEFAULT };
const AFFILIATE_PAYMENT_FILTER_ALLOWED = { tab: AFFILIATE_PAYMENT_TABS.map((item) => item.value) };
const AFFILIATE_PAYMENT_FILTER_OPTIONS = { allowed: AFFILIATE_PAYMENT_FILTER_ALLOWED };

export function AffiliatePaymentsPage() {
  const paymentFilters = useQueryFilters(AFFILIATE_PAYMENT_FILTER_DEFAULTS, AFFILIATE_PAYMENT_FILTER_OPTIONS);
  const tab = paymentFilters.values.tab as TabValue;

  const summaryQuery = useQuery({
    queryKey: ["affiliate-earnings-summary"],
    queryFn: earningsApi.summary,
  });

  const payoutsQuery = useInfinitePage(["affiliate-payouts", tab], (page, pageSize) =>
    earningsApi.payouts({ status: tab, page, page_size: pageSize }),
  );

  const summary = summaryQuery.data;
  const payouts = payoutsQuery.items;
  const isLoading = summaryQuery.isLoading || payoutsQuery.isLoading;

  const { sorted: sortedPayouts, sortKey, direction, toggle } = useTableSort(payouts, {
    candidate: (p) => p.placement.candidate_name,
    job: (p) => p.placement.job_title,
    company: (p) => p.placement.company_name,
    amount: (p) => p.net_paise,
    status: (p) => PAYOUT_STATUS_LABELS[p.status] ?? p.status,
    expectedPaid: (p) => {
      const value = p.status === "paid" ? p.paid_at : p.scheduled_for;
      return value ? new Date(value) : null;
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        icon={NAV_ICONS.payments}
        title={NAV_COPY.common.payments}
        subtitle="Track sourcing fees for candidates you referred — pending until the guarantee window clears, then paid out."
      />

      <SimpleGrid cols={{ base: 1, sm: 3 }}>
        <StatCard
          label="Pending"
          value={formatPaise(summary?.pending_paise ?? 0)}
          icon={IconClock}
          color="teal"
          trend={
            <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
              {summary?.pending_count ?? 0} payout{(summary?.pending_count ?? 0) === 1 ? "" : "s"}
            </Text>
          }
        />
        <StatCard
          label="Received"
          value={formatPaise(summary?.received_paise ?? 0)}
          icon={IconCash}
          color="green"
          trend={
            <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
              {summary?.received_count ?? 0} payout{(summary?.received_count ?? 0) === 1 ? "" : "s"}
            </Text>
          }
        />
        <StatCard
          label="Lifetime earnings"
          value={formatPaise(summary?.lifetime_paise ?? 0)}
          icon={IconCoin}
          trend={
            <Text size="sm" c="dimmed" mt="var(--axio-unit-4)">
              Pending + received
            </Text>
          }
        />
      </SimpleGrid>

      <FilterBar>
        <Tabs value={tab} onChange={(value) => paymentFilters.set({ tab: value ?? AFFILIATE_PAYMENT_TAB_DEFAULT })}>
          <Tabs.List>
            {AFFILIATE_PAYMENT_TABS.map((item) => (
              <Tabs.Tab key={item.value} value={item.value}>
                {item.label}
              </Tabs.Tab>
            ))}
          </Tabs.List>
        </Tabs>
      </FilterBar>

      <DataTableShell
        loading={isLoading}
        error={
          payoutsQuery.isError
            ? payoutsQuery.error instanceof Error
              ? payoutsQuery.error.message
              : undefined
            : undefined
        }
        onRetry={() => void payoutsQuery.refetch()}
        hasMore={payoutsQuery.hasMore}
        loadingMore={payoutsQuery.loadingMore}
        onLoadMore={payoutsQuery.loadMore}
        isEmpty={payouts.length === 0}
        empty={{
          icon: NAV_ICONS.payments,
          title: "No payouts in this category yet",
          description: (
            <>
              When a referred candidate is hired, your sourcing fee will show up here.{" "}
              <Text component={Link} to="/affiliate/jobs" span c="teal" fw="var(--axio-font-weight-semibold)">
                Browse jobs
              </Text>
            </>
          ),
        }}
      >
        <Table verticalSpacing="md" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <SortableTh columnKey="candidate" sortKey={sortKey} direction={direction} onSort={toggle}>
                Candidate
              </SortableTh>
              <SortableTh columnKey="job" sortKey={sortKey} direction={direction} onSort={toggle}>
                Job
              </SortableTh>
              <SortableTh columnKey="company" sortKey={sortKey} direction={direction} onSort={toggle}>
                Company
              </SortableTh>
              <SortableTh columnKey="amount" sortKey={sortKey} direction={direction} onSort={toggle}>
                Amount
              </SortableTh>
              <SortableTh columnKey="status" sortKey={sortKey} direction={direction} onSort={toggle}>
                Status
              </SortableTh>
              <SortableTh columnKey="expectedPaid" sortKey={sortKey} direction={direction} onSort={toggle}>
                Expected / Paid
              </SortableTh>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {sortedPayouts.map((p) => (
              <Table.Tr key={p.id}>
                <Table.Td fw="var(--axio-font-weight-medium)">{p.placement.candidate_name}</Table.Td>
                <Table.Td>{p.placement.job_title}</Table.Td>
                <Table.Td>{p.placement.company_name}</Table.Td>
                <Table.Td fw="var(--axio-font-weight-semibold)">{formatPaise(p.net_paise)}</Table.Td>
                <Table.Td>
                  <Badge color={payoutStatusColor(p.status)} variant="light">
                    {PAYOUT_STATUS_LABELS[p.status] ?? p.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {p.status === "paid" ? formatDate(p.paid_at) : formatDate(p.scheduled_for)}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </DataTableShell>
    </Stack>
  );
}
